import { FunctionComponent, memo, useEffect, useState, useMemo } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { IPracticeExam } from "./IPracticeExam";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { addPracticeExam } from "../../Redux/Slices/PracticeExam/AddPracticeExamSlice";
import { EditPracticeExam } from "../../Redux/Slices/PracticeExam/EditPracticeExamSlice";
import { listQuestionMapping } from "../../Redux/Slices/Questions/QuestionMappingSlice";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import {
  Button,
  TextField,
  IconButton,
  Box,
  Typography,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  Stack,
  Grid,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import NumberInputField from "../../Components/NumberInput/NumberInputField";
import { listQuestionSubType } from "../../Redux/Slices/QuestionSubType/ListQuestionSubTypeSlice";
import PredefinedMappingLayout2 from "../Questions/PredefinedMappingLayout2";
import ResponseDialog from "../../Components/ResponseDialog/ResponseDialog";
import { IAPIResponse } from "../../Redux/interfaces/IAPIResponse";
import AlterDialog from "../../Components/AlterDialog/AlterDialog";
import { MessageDialogProps } from "../../Components/AlterDialog/IAlterDialog";
import TextAreaInputField from "../../Components/TextAreaInput/TextAreaInputField";
interface PracticeExamFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: IPracticeExam;
  setInitialValues: React.Dispatch<React.SetStateAction<IPracticeExam>>;
  onFullScreenToggle?: (full: boolean) => void;
}

const PracticeExamForm: FunctionComponent<PracticeExamFormProps> = (props) => {
  const dispatch = useAppDispatch();

  // Fetching question types from Redux

  const { data: listPracticeExams } = useAppSelector(
    (state) => state.ListPracticeExamSlice,
  );

  const PracticeExam = Array.isArray(listPracticeExams?.data)
    ? listPracticeExams?.data.map((user, index) => ({
      ...user,
      index: index + 1,
    }))
    : [];

  const { data: questionMappingData } = useAppSelector(
    (state) => state.QuestionMappingSlice,
  );

  const { data: listQuestions } = useAppSelector(
    (state) => state.ListQuestionTypeSlice,
  );
  const QuestionList = useMemo(() => {
    return Array.isArray(listQuestions?.data)
      ? listQuestions?.data
        ?.filter((role) => role.Status !== false)
        .map((listQuestions) => ({
          label: listQuestions?.name!,
          value: listQuestions.id,
        }))
      : [];
  }, [listQuestions?.data]);

  const filteredQuestionTypeList = QuestionList?.filter(
    (questionType) =>
      !PracticeExam?.some(
        (item) => item?.question_type_id === questionType?.value,
      ),
  );

  // Map filtered list to required format
  const QuestionFInalList = filteredQuestionTypeList?.map((questionType) => ({
    label: questionType.label!,
    value: questionType.value,
  }));

  const userSchema = z.object({
    id: z.number().optional(),
    name: z
      .string({ message: "Please enter exam name" })
      .min(1, { message: "Please enter exam name" })
      .max(100, { message: "name cannot exceed 100 characters" }),
    question_type_id: z.coerce.number({
      required_error: "Please select question type",
      invalid_type_error: "Please select question type",
    }).min(1, { message: "Please select question type" }),
    question_count: z.coerce
      .number({ message: "Please enter question counts" })
      .min(1, { message: "Please enter question count" })
      .max(1000, { message: "count cannot exceed 1000 " }),
    description: z
      .string({ message: "Please enter description" })
      .min(1, { message: "Please enter description" })
      .max(500, { message: "description cannot exceed 500 characters" }),
    status: z.boolean({
      required_error: "Please select status",
      invalid_type_error: "Please select status",
    }),
    duration: z.coerce
      .number({ message: "Please enter the duration of the exam" })
      .refine((val) => val > 0, {
        message: "PracticeExam duration must be greater than 0 mintues",
      }),
    instruction: z
      .array(z.string().min(1).max(1000))
      .min(1, "Add at least one instruction")
      .max(20, "Cannot add more than 20 instructions"),
    free_tier: z.boolean().optional(),

    subtype_id: z.any().optional(),
    sequence: z.any(),
    subtype_sequence: z.any().optional(),
    question_selection_type: z.enum(["random", "predefined"]).optional(),
    question_mappings: z.array(z.number()).optional(),
    question_sequences: z.any().optional(),
  });

  const methods = useForm<IPracticeExam>({
    defaultValues: {
      ...props.initialValues,
      instruction: props.initialValues.instruction ?? [],
      status: props.initialValues.status ?? true,
      question_selection_type: (props.initialValues.id || props.initialValues.name)
        ? props.initialValues.question_selection_type ||
        (props.initialValues.question_mappings && props.initialValues.question_mappings?.length > 0
          ? "predefined"
          : "random")
        : "random",
      question_mappings: props.initialValues?.question_mappings || [],
    },
    resolver: zodResolver(userSchema),
    mode: "onChange",
  });

  useEffect(() => {
    console.log("UE4");
    return () => {
      props.setInitialValues({});
    };
  }, []);

  // Status list
  const PracticeExamSatus = [
    {
      label: "Active",
      value: true,
    },

    {
      label: "Inactive",
      value: false,
    },
  ];

  const { setValue, getValues } = methods;
  const [instructionInput, setInstructionInput] = useState<string>("");
  // const [instructions, setInstructions] = useState<string[]>([]); // Removing unused state
  const [instructionError, setInstructionError] = useState("");
  const [selectedQuestions, setSelectedQuestions] = useState<number[]>([]);
  const [alertResponse, setAlertResponse] = useState<IAPIResponse<any>>(
    {} as IAPIResponse<any>,
  );
  const [editingSub, setEditingSub] = useState<number | null>(null);
  const [showSwitchWarning, setShowSwitchWarning] = useState(false);
  const [pendingSwitchData, setPendingSwitchData] = useState<{
    type: "type" | "subtype";
    value: any;
  } | null>(null);

  const [isDataInitialized, setIsDataInitialized] = useState(false);

  useEffect(() => {
    setIsDataInitialized(false);
  }, [props.initialValues.id]);

  // allStagedIds moved to after displayDataListsub declaration

  const selectionType = methods.watch("question_selection_type");
  useEffect(() => {
    props.onFullScreenToggle?.(selectionType === "predefined");
    console.log("UE1");
  }, [selectionType, props.onFullScreenToggle]);

  const handleSwitchConfirm = (action: "add" | "discard") => {
    if (!pendingSwitchData) return;

    if (action === "add") {
      const subtypeId = methods.watch("subtype_id");
      if (subtypeId) {
        handleAddDatasub();
      }
      // If no subtype but selected questions, they are usually handled by onSubmit
      // but if the user is switching main types and wants to "add",
      // in PracticeExam it's either subtypes OR main selection.
      // If they were selecting for a main type and switch, "adding" doesn't make sense
      // unless they are using subtypes.
    }

    // Perform the switch
    if (pendingSwitchData.type === "type") {
      methods.setValue(
        "question_type_id",
        pendingSwitchData.value ?? undefined,
        {
          shouldValidate: true,
          shouldDirty: true,
        },
      );
      dispatch(
        listQuestionSubType({ questiontype_id: pendingSwitchData.value }),
      );
      setDisplayDataListsub([]);
      methods.setValue("subtype_id", null);
    } else {
      methods.setValue("subtype_id", pendingSwitchData.value, {
        shouldValidate: true,
      });
    }

    if (action === "discard") {
      setSelectedQuestions([]);
    }

    setShowSwitchWarning(false);
    setPendingSwitchData(null);
  };

  useEffect(() => {
    if (props.initialValues.question_mappings) {
      setSelectedQuestions(props.initialValues.question_mappings || []);
      console.log("UE2");
    }
  }, [props.initialValues.question_mappings]);

  const handleAddInstruction = () => {
    if (instructionError) return;

    const currentInstructions = getValues("instruction") || [];

    if (instructionInput.trim() && currentInstructions.length < 20) {
      const updatedInstructions = [...currentInstructions, instructionInput];
      // setInstructions(updatedInstructions);
      setValue("instruction", updatedInstructions, { shouldValidate: true });
      setInstructionInput("");
    }
  };

  // Delete instruction handler
  const handleDeleteInstruction = (index: number) => {
    const currentInstructions = getValues("instruction");
    const updatedInstructions =
      currentInstructions && currentInstructions?.filter((_, i) => i !== index);
    // setInstructions(updatedInstructions!); // Update local state
    setValue("instruction", updatedInstructions); // Update instructions field
    methods.trigger("instruction"); // Force re-render to reflect changes
  };

  const { data: listQuestionssub } = useAppSelector(
    (state) => state.ListQuestionSubTypeSlice,
  );
  const QuestionsubList = useMemo(() => {
    return Array.isArray(listQuestionssub?.data)
      ? listQuestionssub?.data
        ?.filter((role) => role.status !== false) // Filter out roles where status is false
        .map((listQuestions) => ({
          label: listQuestions?.subtype_name!,
          value: listQuestions.id,
        }))
      : [];
  }, [listQuestionssub?.data]);
  const [displayDataListsub, setDisplayDataListsub] = useState<
    {
      subtype_id?: { label: string; value: number } | null;
      sub_question_count?: number | null;
      sequence?: number | null;
      question_ids?: number[];
      free_tier?: boolean;
      questionTypes?: { label: string; value: number } | null;
      subtypes?: { label: string; value: number } | null;
      subtype_counts?: number | null;
    }[]
  >([]);
  const [displayDataList, setDisplayDataList] = useState<any[]>([]);
  const [editingMain, setEditingMain] = useState<number | null>(null);

  const allStagedIds = useMemo(() => {
    const subIds = displayDataListsub
      .filter((_, index) => index !== editingSub)
      .flatMap((item) => (item as any).question_ids || []);

    const mainIds = displayDataList
      .filter((_, index) => index !== editingMain)
      .flatMap((item) => item.question_ids || []);

    return Array.from(new Set([...subIds, ...mainIds]));
  }, [displayDataListsub, editingSub, displayDataList, editingMain]);

  const questionTypessub = methods.getValues("subtype_id");
  const isDuplicatesub = displayDataListsub.some(
    (item) => item.subtype_id?.value === questionTypessub,
  );

  const handleAddDatasub = () => {
    const subtypeId = methods.getValues("subtype_id"); // Get selected subtype value
    const isPredefined =
      methods.getValues("question_selection_type") === "predefined";
    const subtypeCount =
      methods.getValues("sub_question_count") ||
      (isPredefined ? methods.getValues("question_count") : null);
    const subtypeSequence =
      methods.getValues("subtype_sequence") || (isPredefined ? 1 : null);

    if (!subtypeCount) {
      setAlertResponse({
        status: false,
        message: "Please enter a question count.",
        data: null,
        status_code: 400,
      });
      return false;
    }

    if (!subtypeId && !isPredefined) {
      setAlertResponse({
        status: false,
        message: "Please select a subtype.",
        data: null,
        status_code: 400,
      });
      return false;
    }

    const selectedSubtype = subtypeId
      ? QuestionsubList?.find((list) => list.value === subtypeId)
      : { label: "General", value: 0 };

    if (!selectedSubtype && subtypeId) {
      setAlertResponse({
        status: false,
        message: "Selected subtype not found.",
        data: null,
        status_code: 400,
      });
      return false;
    }

    const free_tier = methods.getValues("free_tier");
    const isDuplicate = displayDataListsub.some(
      (item: any, index: number) =>
        item.subtype_id?.value === (subtypeId || 0) && index !== editingSub,
    );

    if (isDuplicate) {
      setAlertResponse({
        status: false,
        message: "This subtype (or General) is already in the list.",
        data: null,
        status_code: 400,
      });
      return false;
    }

    if (editingSub !== null) {
      setDisplayDataListsub((prevList: any) =>
        prevList.map((item: any, i: any) =>
          i === editingSub
            ? {
              subtype_id: selectedSubtype,
              sub_question_count: subtypeCount,
              sequence: subtypeSequence,
              free_tier: !!free_tier,
              question_ids: selectedQuestions || [],
              questionTypes: selectedSubtype
                ? {
                  label: selectedSubtype.label,
                  value: selectedSubtype.value,
                }
                : undefined, // Add this line, though subtype usually implies type is derived or separate. Wait, the card uses data?.questionTypes?.label.
              // In ExamModelForm (viewed previously), subtypes are grouped under main types.
              // Here, we select a subtype directly.
              // We need the Main Question Type for the card display?
              // The form has "question_type_id". We should grab that.
            }
            : item,
        ),
      );
      setEditingSub(null);
    } else {
      // Get current question type
      const currentQTypeID = methods.getValues("question_type_id");
      const currentQType = QuestionList?.find(
        (q) => q.value === currentQTypeID,
      );

      setDisplayDataListsub((prevList: any) => {
        return [
          ...prevList,
          {
            subtype_id: selectedSubtype,
            sub_question_count: subtypeCount,
            sequence: subtypeSequence,
            free_tier: !!free_tier,
            question_ids: selectedQuestions || [],
            questionTypes: currentQType, // Add this
          },
        ];
      });
    }

    // Reset fields
    methods.resetField("subtype_id");
    methods.resetField("sub_question_count", { defaultValue: "" });
    methods.resetField("subtype_sequence", { defaultValue: "" as any });
    methods.resetField("free_tier", { defaultValue: false });
    return true; // Return success
  };

  // Delete data by index
  const handleEditSub = (index: number) => {
    const item = displayDataListsub[index];
    methods.setValue("subtype_id", item.subtype_id?.value);
    methods.setValue("sub_question_count", item.sub_question_count);
    methods.setValue("subtype_sequence", item.sequence);
    methods.setValue("free_tier", item.free_tier);
    setSelectedQuestions(item.question_ids || []);
    setEditingSub(index);
    setEditingMain(null);
    methods.setValue("subtype_sequence", item.sequence);
  };

  const handleEditMain = (index: number) => {
    const item = displayDataList[index];
    methods.setValue(
      "question_type_id",
      item.questionTypes?.value ?? undefined,
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );
    methods.setValue("question_count", item.question_counts);
    methods.setValue("duration", item.durations);
    methods.setValue("sequence", item.sequence); // Main Sequence

    // Clear Subtype fields
    methods.setValue("subtype_id", null);
    methods.setValue("sub_question_count", "");

    setSelectedQuestions(item.question_ids || []);
    setEditingMain(index);
    setEditingSub(null);
  };

  const handleDeleteMain = (index: number) => {
    const itemToDelete = displayDataList[index];
    setDisplayDataList((prevList) => prevList.filter((_, i) => i !== index));
    // Update internal form state to keep in sync
    const currentSubtypeData = methods.getValues("subtype_data") || [];
    methods.setValue(
      "subtype_data",
      currentSubtypeData.filter(
        (sub: any) =>
          !(
            sub.subtype_id === 0 &&
            Number(sub.sequence) === Number(itemToDelete.sequence)
          ),
      ),
    );
  };

  const handleDeleteDatasub = (index: number) => {
    const itemToDelete = displayDataListsub[index];
    setDisplayDataListsub((prevList) => prevList.filter((_, i) => i !== index));
    // Update internal form state to keep in sync
    const currentSubtypeData = methods.getValues("subtype_data") || [];
    methods.setValue(
      "subtype_data",
      currentSubtypeData.filter(
        (sub: any) => !(sub.subtype_id === itemToDelete.subtype_id?.value),
      ),
    );
  };
  const handleAddMain = () => {
    const questionTypeId = methods.getValues("question_type_id");
    const questionCount = methods.getValues("question_count");
    const duration = methods.getValues("duration");
    const sequence = methods.getValues("sequence");

    if (!questionCount) {
      setAlertResponse({
        status: false,
        message: "Please enter a question count.",
        data: null,
        status_code: 400,
      });
      return;
    }

    const selectedQuestionType = QuestionList?.find(
      (q) => q.value === questionTypeId,
    );

    if (editingMain !== null) {
      setDisplayDataList((prev) =>
        prev.map((item, i) =>
          i === editingMain
            ? {
              questionTypes: selectedQuestionType,
              question_counts: questionCount,
              durations: duration,
              sequence: sequence,
              question_ids: selectedQuestions,
            }
            : item,
        ),
      );
      setEditingMain(null);
    } else {
      setDisplayDataList((prev) => [
        ...prev,
        {
          questionTypes: selectedQuestionType,
          question_counts: questionCount,
          durations: duration,
          sequence: sequence,
          question_ids: selectedQuestions,
        },
      ]);
    }

    // Reset
    methods.setValue("subtype_sequence", "" as any);
    setSelectedQuestions([]);
  };

  const questionCount: any = methods.getValues("question_count");

  const totalSubQuestionCount =
    (displayDataList?.reduce(
      (acc, item) => acc + (Number(item.question_counts) || 0),
      0,
    ) || 0) +
    (displayDataListsub?.reduce(
      (acc, item: any) => acc + (Number(item?.sub_question_count) || 0),
      0,
    ) || 0);
  console.log("totalSubQuestionCount", totalSubQuestionCount);
  console.log("displayDataListsub", displayDataListsub);

  useEffect(() => {
    if (methods.getValues("id") && !isDataInitialized) {
      const subtypes: any = methods.getValues("subtype_data");
      const currentQTypeID = methods.getValues("question_type_id");

      // Wait until lookup data is ready
      if (!QuestionList || QuestionList.length === 0) return;
      const hasSubtypes = subtypes?.some(
        (sub: any) => sub.subtype_id !== 0 && sub.subtype_id,
      );
      if (hasSubtypes && (!QuestionsubList || QuestionsubList.length === 0)) return;

      const currentQType = QuestionList?.find(
        (q) => q.value === currentQTypeID,
      );

      const generalData =
        subtypes
          ?.filter((sub: any) => sub.subtype_id === 0 || !sub.subtype_id)
          .map((sub: any) => {
            const ids = sub.question_ids || props.initialValues.question_mappings || [];
            const seqs = sub.question_sequences || props.initialValues.question_sequences || [];
            
            let sortedIds = [...ids];
            if (ids.length === seqs.length && seqs.length > 0) {
              const pairs = ids.map((id: any, i: any) => ({ id, seq: Number(seqs[i]) }));
              pairs.sort((a: any, b: any) => a.seq - b.seq);
              sortedIds = pairs.map((p: any) => p.id);
            }

            return {
              questionTypes: currentQType,
              question_counts: sub.sub_question_count,
              durations: methods.getValues("duration"),
              sequence: sub.sequence,
              question_ids: sortedIds,
              question_sequences: seqs,
            };
          }) || [];

      const subtypeData =
        subtypes
          ?.filter((sub: any) => sub.subtype_id !== 0 && sub.subtype_id)
          .map((sub: any) => {
            const matchingSubtype: any = QuestionsubList?.find(
              (d: any) => d.value === sub.subtype_id,
            );
            
            const ids = sub.question_ids || [];
            const seqs = sub.question_sequences || props.initialValues.question_sequences || [];

            let sortedIds = [...ids];
            if (ids.length === seqs.length && seqs.length > 0) {
              const pairs = ids.map((id: any, i: any) => ({ id, seq: Number(seqs[i]) }));
              pairs.sort((a: any, b: any) => a.seq - b.seq);
              sortedIds = pairs.map((p: any) => p.id);
            }

            return {
              subtype_id: {
                label: matchingSubtype?.label || "Unknown",
                value: sub.subtype_id,
              },
              sub_question_count: sub.sub_question_count,
              sequence: sub.sequence,
              questionTypes: currentQType,
              question_ids: sortedIds,
              question_sequences: seqs,
            };
          }) || [];

      setDisplayDataList(generalData);
      setDisplayDataListsub(subtypeData);
      setIsDataInitialized(true);
    }
  }, [
    methods,
    QuestionsubList,
    QuestionList,
    props.initialValues.id,
    props.initialValues.question_sequences,
    isDataInitialized,
  ]);

  const onSubmit: SubmitHandler<any> = (val) => {
    const isPredefined = val.question_selection_type === "predefined";
    const limit =
      (methods.watch("sub_question_count") as number) ||
      (methods.watch("question_count") as number) ||
      0;

    // Simplified onSubmit logic to remove strict count checks
    const targetCount = limit; // Keep reference if needed for other logic

    const finalSubtypeData = [
      ...displayDataList.map((qt: any) => ({
        subtype_id: 0, // General
        sub_question_count: Number(qt?.question_counts || 0),
        sequence: Number(qt?.sequence || 0),
        question_ids: qt.question_ids || [],
        question_sequences: (qt.question_ids || []).map((_: any, index: number) => index + 1),
      })),
      ...displayDataListsub.map((qt: any) => ({
        subtype_id: qt?.subtype_id?.value || 0,
        sub_question_count: Number(qt?.sub_question_count || 0),
        sequence: Number(qt?.sequence || 0),
        question_ids: qt.question_ids || [],
        question_sequences: (qt.question_ids || []).map((_: any, index: number) => index + 1),
      })),
    ];

    const examData = {
      ...(val.id && { id: val.id }),
      name: val.name,
      question_type_id: val.question_type_id,
      duration: Number(val.duration || 0),
      question_count: Number(val.question_count || 0),
      description: val.description,
      instruction: val.instruction,
      status: val.status,
      subtype_data: finalSubtypeData,

      question_selection_type: val.question_selection_type,
      question_ids:
        (val.question_selection_type === "predefined" && finalSubtypeData.length === 0)
          ? Array.from(new Set([...selectedQuestions]))
          : [],
      question_sequences: (val.question_selection_type === "predefined" && finalSubtypeData.length === 0)
        ? selectedQuestions.map((_: any, index: number) => index + 1)
        : [],
    };

    if (
      (displayDataListsub.length >= 1 || displayDataList.length >= 1) &&
      (methods.formState.isValid || true) // Relaxed check, trust the button state or user intent
    ) {
      if (val.question_selection_type === "predefined") {
        const totalSetQuestions =
          displayDataList.reduce(
            (acc, item) => acc + (Number(item.question_counts) || 0),
            0,
          ) +
          displayDataListsub.reduce(
            (acc, item) => acc + (Number(item.sub_question_count) || 0),
            0,
          );

        if (totalSetQuestions !== Number(val.question_count || 0)) {
          setAlertResponse({
            status: false,
            message: `Total Question Count (${val.question_count}) does not match the sum of selected sets (${totalSetQuestions}).`,
            data: null,
            status_code: 400,
          });
          return;
        }
      }
      console.log("examData", examData);

      val.id
        ? dispatch(EditPracticeExam(examData))
        : dispatch(addPracticeExam(examData));
    } else if (
      displayDataListsub.length === 0 &&
      displayDataList.length === 0
    ) {
      // Allow saving even if empty? ExamModel allows it if name is present.
      // The original code had specific checks.
      // Let's rely on standard form validation + existence of at least 1 item if predefined.
      if (
        val.question_selection_type === "predefined" &&
        displayDataList.length === 0 &&
        displayDataListsub.length === 0
      ) {
        setAlertResponse({
          status: false,
          message: "Please add at least one question set.",
          data: null,
          status_code: 400,
        });
        return;
      }
      val.id
        ? dispatch(EditPracticeExam(examData))
        : dispatch(addPracticeExam(examData));
    }
  };
  const FIlterdata: any = QuestionFInalList?.filter((data) => {
    return data?.value === methods.getValues("question_type_id");
  });

  const instructionList = methods.watch("instruction");

  return (
    <>
      <FormProvider {...methods}>
        <form
          ref={props.formRef}
          onSubmit={methods.handleSubmit(onSubmit, (errors) =>
            console.log("Validation Errors:", errors),
          )}
        >
          <div>
            {methods.watch("question_selection_type") === "predefined" ? (
              <Box sx={{ mb: 4 }}>
                <PredefinedMappingLayout2
                  selectedIds={selectedQuestions}
                  onSelectionChange={setSelectedQuestions}
                  stagedIds={allStagedIds}
                  onAdd={() => {
                    const subtypeId = methods.getValues("subtype_id");
                    if (subtypeId) {
                      const added = handleAddDatasub() as unknown as boolean;
                      if (added) setSelectedQuestions([]);
                    } else {
                      setAlertResponse({
                        status: false,
                        message: "Please select a subtype.",
                        data: null,
                        status_code: 400,
                      });
                    }
                  }}
                  displayDataListsub={displayDataListsub}
                  onDeleteSub={handleDeleteDatasub}
                  onEditSub={handleEditSub}
                  excludeId={methods.getValues("id") ? methods.getValues("id") : props.initialValues.id}
                  excludeType="test"
                  statusOptions={PracticeExamSatus}
                  isEditing={editingSub !== null || editingMain !== null}
                  displayDataList={displayDataList}
                  onEditMain={handleEditMain}
                  onDeleteMain={handleDeleteMain}
                />
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} style={{ marginTop: "15px" }}>
                  <TextInputFieldC
                    name="name"
                    label="Practice Exam Name"
                    type="text"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Question Selection Type
                    </Typography>
                    <RadioGroup
                      row
                      value={methods.watch("question_selection_type")}
                      onChange={(e) => {
                        methods.setValue(
                          "question_selection_type",
                          e.target.value as "random" | "predefined",
                          { shouldValidate: true },
                        );
                        if (e.target.value === "random") {
                          setSelectedQuestions([]);
                        }
                      }}
                    >
                      <FormControlLabel
                        value="random"
                        control={<Radio />}
                        label="Random Questions"
                      />
                      <FormControlLabel
                        value="predefined"
                        control={<Radio />}
                        label="Predefined Questions"
                      />
                    </RadioGroup>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Select
                    name="question_type_id"
                    label="Question Type"
                    options={QuestionFInalList ?? []}
                    disabled={
                      displayDataList.length > 0 || displayDataListsub.length > 0
                    }
                    value={
                      QuestionList?.find(
                        (list) =>
                          list.value === methods.getValues("question_type_id"),
                      ) ?? null
                    }
                    onChange={(_, value) => {
                      const val = value as AutocompleteOption;
                      if (selectedQuestions.length > 0) {
                        setPendingSwitchData({
                          type: "type",
                          value: val?.value,
                        });
                        setShowSwitchWarning(true);
                        return;
                      }
                      methods.setValue(
                        "question_type_id",
                        val?.value ?? undefined,
                        {
                          shouldValidate: true,
                          shouldDirty: true,
                        },
                      );
                      dispatch(
                        listQuestionSubType({ questiontype_id: val?.value }),
                      );
                      setDisplayDataListsub([]);
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <NumberInputField
                    type="number"
                    name="question_count"
                    label="Question Count"
                    fullWidth
                    inputProps={{
                      max: FIlterdata[0]?.label === "Writing" ? 1 : 100,
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextAreaInputField
                    name="description"
                    label="Description"
                    type="text"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <NumberInputField
                    name="duration"
                    label="Duration in Minutes"
                    type="number"
                    sx={{ width: "100%" }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Select
                    name="status"
                    label="Status"
                    options={PracticeExamSatus}
                    value={
                      PracticeExamSatus?.find(
                        (role) => role.value === methods.getValues("status"),
                      ) ?? null
                    }
                    onChange={(_, value) => {
                      const val = value as AutocompleteOption;
                      methods.setValue("status", val?.value, {
                        shouldValidate: true,
                      });
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={methods.watch("free_tier") === true}
                        onChange={() => {
                          methods.setValue("free_tier", !methods.watch("free_tier"), {
                            shouldValidate: true,
                          });
                        }}
                      />
                    }
                    label="Free Tier"
                  />
                </Grid>
              </Grid>
            )}

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                marginTop: "30px",
              }}
            >
              {/* Instruction Input Field with TextArea */}
              <Box display="flex">
                <TextField
                  label="Instruction"
                  value={instructionInput}
                  onChange={(e) => {
                    const value = e.target.value;

                    // live validation
                    if (value.length > 1000) {
                      setInstructionError(
                        "Instruction cannot exceed 1000 characters",
                      );
                    } else if (value.trim().length === 0) {
                      setInstructionError("Please enter the instruction");
                    } else {
                      setInstructionError("");
                    }

                    setInstructionInput(value);
                  }}
                  fullWidth
                  multiline
                  error={instructionError !== ""}
                  helperText={instructionError}
                  minRows={1}
                  maxRows={4}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    handleAddInstruction();
                    methods.trigger("instruction");
                  }}
                  sx={{
                    ml: 1,
                    width: "10px",
                    height: "4rem",
                    bgcolor: "primary.main", // Set the background color to primary
                    ":hover": {
                      bgcolor: "primary.main", // Keep the hover background color the same
                    },
                  }}
                >
                  <AddIcon sx={{ color: "white" }} />
                </Button>
              </Box>

              {/* Render Instructions */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(400px, 1fr))",
                  gap: "20px",
                }}
              >
                {instructionList?.map((instruction, index) => (
                  <Box
                    key={index}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    bgcolor="lightgrey"
                    p={0.3}
                    my={1}
                    borderRadius="4px"
                    sx={{
                      marginBottom: "0px",
                      marginTop: "0px",

                      ":hover button": { visibility: "visible" }, // Visibility for smoother transitions
                    }}
                  >
                    <Typography
                      sx={{
                        flexGrow: 1, // Make text take available space
                        wordWrap: "break-word", // Ensure long text wraps
                        overflow: "hidden",
                        padding: "10px",
                      }}
                    >
                      {instruction}
                    </Typography>
                    <IconButton onClick={() => handleDeleteInstruction(index)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </div>
            </div>
          </div>
          {methods.formState.errors.instruction && (
            <Typography color="error" sx={{ mt: 1 }}>
              {methods.formState.errors.instruction.message}
            </Typography>
          )}
          {methods.watch("question_selection_type") !== "predefined" && (
            <>
              <div className="twofieldlist" style={{ marginTop: "20px" }}>
                {/* Select for Question Type */}
                <Select
                  name="subtype_id"
                  label="Question Subtype"
                  sx={{ width: "50%" }}
                  options={QuestionsubList ?? []}
                  disabled={(QuestionsubList?.length ?? 0) < 1}
                  value={
                    QuestionsubList?.find(
                      (list) => list.value === methods.getValues("subtype_id"),
                    ) ?? null
                  }
                  onChange={(_, value) => {
                    const val = value as AutocompleteOption;
                    if (selectedQuestions.length > 0) {
                      setPendingSwitchData({
                        type: "subtype",
                        value: val?.value,
                      });
                      setShowSwitchWarning(true);
                      return;
                    }
                    methods.setValue("subtype_id", val?.value, {
                      shouldValidate: true,
                    });
                  }}
                />

                {/* Input for Question Counts */}
                <NumberInputField
                  type="number"
                  disabled={(QuestionsubList?.length ?? 0) < 1}
                  sx={{ width: "50%" }}
                  name="sub_question_count"
                  label="Question Subcount"
                />

                <NumberInputField
                  type="number"
                  disabled={(QuestionsubList?.length ?? 0) < 1}
                  sx={{ width: "50%" }}
                  name="sequence"
                  label="Sequence"
                />
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  {/* <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!methods.watch("free_tier")}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => methods.setValue("free_tier", e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">Free Tier</Typography>}
                  /> */}
                  {(QuestionsubList?.length ?? 0) < 1 ? (
                    <AddCircleOutlineIcon
                      sx={{ fontSize: "30px", color: "gray" }}
                    />
                  ) : (
                    <AddCircleOutlineIcon
                      titleAccess={
                        isDuplicatesub === true
                          ? "Question Subtype already exists .Please add another Question Subtype"
                          : ""
                      }
                      sx={{
                        fontSize: "30px",
                        color: "#009DFF",
                        cursor: "pointer",
                      }}
                      onClick={handleAddDatasub}
                    />
                  )}
                </div>
              </div>
            </>
          )}
        </form>
        {methods.watch("question_selection_type") !== "predefined" &&
          displayDataListsub.length > 0 && (
            <div
              style={{
                marginTop: "20px",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(45%, 1fr))", // 2 columns
                gap: "10px",
              }}
            >
              {displayDataListsub.map((data, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "10px",
                    backgroundColor: "lightgrey",
                    borderRadius: "5px",
                    padding: "5px",
                    // width: "50%",
                    boxShadow: "0px 3px 9px darkgray",
                  }}
                >
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {data?.questionTypes?.label} -{" "}
                      {data?.subtype_id?.label || data?.subtypes?.label}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {data?.subtype_counts || data?.sub_question_count}{" "}
                      Questions • Predefined
                    </Typography>
                    <div>
                      <b>Sequence :</b> {data?.sequence}{" "}
                    </div>
                    <div>
                      <b>Free Tier :</b> {data?.free_tier ? "Yes" : "No"}{" "}
                    </div>
                  </Box>

                  {/* Delete button for each entry */}
                  <IconButton onClick={() => handleDeleteDatasub(index)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </div>
              ))}
            </div>
          )}
        <div>
          {totalSubQuestionCount !== 0 ? (
            String(totalSubQuestionCount) === String(questionCount) ? (
              ""
            ) : totalSubQuestionCount > questionCount ? (
              <p style={{ color: "red" }}>
                Total question count across subtypes must match the required
                count for the question type. Please remove{" "}
                {totalSubQuestionCount - questionCount} to align.
              </p>
            ) : (
              <p style={{ color: "red" }}>
                Total question count across subtypes must match the required
                count for the question type. Please add{" "}
                {questionCount - totalSubQuestionCount} more.
              </p>
            )
          ) : null}
        </div>
        {methods.watch("question_selection_type") !== "predefined" && (
          <div style={{ color: "#009dff", fontSize: "15px", textAlign: "center", marginTop: "20px", width: "100%" }}>
            (Note: Click Free Tier to show exam to free users)
          </div>
        )}
      </FormProvider>
      <ResponseDialog response={alertResponse} />
      <AlterDialog
        open={showSwitchWarning}
        setOpen={setShowSwitchWarning}
        alterheading="Unsaved Selections"
        alterableName="You have selected questions that haven't been added to the model yet. What would you like to do?"
        buttonname={
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              width: "100%",
            }}
          >
            <Button
              variant="contained"
              onClick={() => handleSwitchConfirm("add")}
              sx={{ bgcolor: "#009DFF", "&:hover": { bgcolor: "#007acc" } }}
            >
              Add & Switch
            </Button>
            <Button
              variant="outlined"
              onClick={() => handleSwitchConfirm("discard")}
              sx={{ color: "#009DFF", borderColor: "#009DFF" }}
            >
              Discard & Switch
            </Button>
            <Button
              variant="text"
              onClick={() => {
                setShowSwitchWarning(false);
                setPendingSwitchData(null);
              }}
              sx={{ color: "gray" }}
            >
              Cancel
            </Button>
          </Box>
        }
      />
    </>
  );
};

export default memo(PracticeExamForm);
